/**
 * UI primitives
 * Thin wrappers over the classes in index.css so pages read as structure
 * rather than markup.
 */

import React from 'react';
import { Icon, ICONS } from '../shell/icons';

type IconKey = keyof typeof ICONS;

/* ------------------------------------------------------------------ page */

export const PageHead: React.FC<{
  eyebrow: string;
  title: string;
  lede?: string;
  actions?: React.ReactNode;
}> = ({ eyebrow, title, lede, actions }) => (
  <div className="ph">
    <div>
      <span className="eye">{eyebrow}</span>
      <h3>{title}</h3>
      {lede && <p>{lede}</p>}
    </div>
    {actions && <div className="pa">{actions}</div>}
  </div>
);

export const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="sec-t">{children}</div>
);

/* ------------------------------------------------------------------ card */

export const Card: React.FC<{
  title?: string;
  sub?: string;
  right?: React.ReactNode;
  flush?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ title, sub, right, flush, className, children }) => (
  <div className={`card${className ? ' ' + className : ''}`}>
    {title && (
      <div className="card-hd">
        <h4>{title}</h4>
        {sub && <span className="s">{sub}</span>}
        {right && <div className="r">{right}</div>}
      </div>
    )}
    <div className={`card-b${flush ? ' flush' : ''}`} style={flush ? { padding: '0 0 6px' } : undefined}>
      {children}
    </div>
  </div>
);

/* ------------------------------------------------------------------- kpi */

export const Kpi: React.FC<{
  label: string;
  value: React.ReactNode;
  unit?: string;
  color?: string;
  icon?: IconKey;
  pip?: string;
  dir?: 'up' | 'dn' | '';
  sub?: string;
  valueColor?: string;
}> = ({ label, value, unit, color = 'var(--blue)', icon = 'chart', pip, dir, sub, valueColor }) => (
  <div className="kpi">
    <div className="kpi-top">
      <span className="kic" style={{ background: `color-mix(in srgb, ${color} 13%, #fff)`, color }}>
        <Icon d={ICONS[icon]} />
      </span>
      {pip && (
        <span className={`pip ${dir === 'up' ? 'pip-g' : dir === 'dn' ? 'pip-r' : 'pip-n'}`}>{pip}</span>
      )}
    </div>
    <div className="v" style={valueColor ? { color: valueColor } : undefined}>
      {value}
      {unit && <small>{unit}</small>}
    </div>
    <div className="k">{label}</div>
    {sub && <div className="sub">{sub}</div>}
  </div>
);

/* ------------------------------------------------------------------ tags */

export type Tone = 'g' | 'b' | 'a' | 'r' | 'p' | 't' | 'k' | 'n' | 'd';

export const Tag: React.FC<{ tone?: Tone; children: React.ReactNode; dot?: boolean }> = ({
  tone = 'n',
  children,
  dot,
}) => (
  <span className={`tg tg-${tone}`}>
    {dot && <span className="dotk" />}
    {children}
  </span>
);

/* --------------------------------------------------------------- pieces */

export const Split: React.FC<{ label: React.ReactNode; value: React.ReactNode; color?: string }> = ({
  label,
  value,
  color,
}) => (
  <div className="split">
    <span>{label}</span>
    <span className="v" style={color ? { color } : undefined}>
      {value}
    </span>
  </div>
);

export const Bar: React.FC<{ pct: number; color?: string; width?: number | string }> = ({
  pct,
  color = 'var(--blue)',
  width,
}) => (
  <span className="bar" style={width ? { display: 'inline-block', width, verticalAlign: 'middle' } : undefined}>
    <i style={{ width: `${pct}%`, background: color }} />
  </span>
);

export const ProgRow: React.FC<{ label: string; value: string; pct: number; color?: string }> = ({
  label,
  value,
  pct,
  color,
}) => (
  <div className="prog-row">
    <span className="l">{label}</span>
    <Bar pct={pct} color={color} width="100%" />
    <span className="v">{value}</span>
  </div>
);

export const Note: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({
  children,
  style,
}) => (
  <div className="note" style={style}>
    {children}
  </div>
);

/* ----------------------------------------------------------------- table */

export interface Col {
  t: string;
  num?: boolean;
}

export const Table: React.FC<{ cols: Col[]; rows: React.ReactNode[][]; maxHeight?: number }> = ({
  cols,
  rows,
  maxHeight,
}) => (
  <div className="tscroll" style={maxHeight ? { maxHeight } : undefined}>
    <table>
      <thead>
        <tr>
          {cols.map((c, i) => (
            <th key={i} className={c.num ? 'num' : undefined}>
              {c.t}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td key={j} className={cols[j]?.num ? 'num' : undefined}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ------------------------------------------------------------ demo badge */

export const DemoBadge: React.FC<{ what?: string }> = ({ what = 'Demo data' }) => (
  <span className="tg tg-p" title="Fixture content - this screen is not wired to a backend yet">
    {what}
  </span>
);
